import React, { useState, useEffect } from 'react';
import { getUserTransactions, addTransaction, updateTransaction, deleteTransaction } from './firebase';

export function TransactionModal({ isOpen, onClose, user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [formData, setFormData] = useState({
    transactionHash: '',
    xlink: ''
  });

  useEffect(() => {
    if (isOpen && user?.uid) {
      loadTransactions();
    }
  }, [isOpen, user?.uid]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const userTransactions = await getUserTransactions(user.uid);
      console.log("**** userTransactions", userTransactions)
      setTransactions(userTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.transactionHash.trim() || !formData.xlink.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      if (editingTransaction) {
        // Update existing transaction
        await updateTransaction(editingTransaction.id, formData);
        setTransactions(prev =>
            prev.map(t =>
                t.id === editingTransaction.id
                    ? { ...t, ...formData, updatedAt: new Date() }
                    : t
            )
        );
        setEditingTransaction(null);
      } else {
        // Add new transaction
        const newTransaction = await addTransaction(user.uid, formData);
        // Add createdAt timestamp to the new transaction
        const transactionWithTimestamp = {
          ...newTransaction,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setTransactions(prev => [...prev, transactionWithTimestamp]);
        setShowAddForm(false);
      }

      setFormData({ transactionHash: '', xlink: '' });
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert('Error saving transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      transactionHash: transaction.transactionHash,
      xlink: transaction.xlink
    });
    setShowAddForm(true);
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteTransaction(transactionId);
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Error deleting transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingTransaction(null);
    setFormData({ transactionHash: '', xlink: '' });
  };

  if (!isOpen) return null;

  return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-2xl shadow-2xl border-4 border-yellow-400 max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-yellow-400 text-black px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Transactions for {user?.email}</h2>
              <p className="text-sm opacity-80">@{user?.twitter_screen_name}</p>
            </div>
            <button
                onClick={onClose}
                className="text-black hover:bg-black hover:text-yellow-400 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Add/Edit Form */}
            {showAddForm && (
                <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-yellow-400">
                  <h3 className="text-xl font-semibold text-yellow-400 mb-4">
                    {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-yellow-300 text-sm font-medium mb-2">
                        Transaction Hash
                      </label>
                      <input
                          type="text"
                          value={formData.transactionHash}
                          onChange={(e) => setFormData(prev => ({ ...prev, transactionHash: e.target.value }))}
                          className="w-full px-3 py-2 bg-gray-700 text-yellow-200 border border-yellow-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-300"
                          placeholder="Enter transaction hash"
                          required
                      />
                    </div>
                    <div>
                      <label className="block text-yellow-300 text-sm font-medium mb-2">
                        X Link
                      </label>
                      <input
                          type="url"
                          value={formData.xlink}
                          onChange={(e) => setFormData(prev => ({ ...prev, xlink: e.target.value }))}
                          className="w-full px-3 py-2 bg-gray-700 text-yellow-200 border border-yellow-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-300"
                          placeholder="Enter X (Twitter) link"
                          required
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                          type="submit"
                          disabled={loading}
                          className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : (editingTransaction ? 'Update' : 'Add')}
                      </button>
                      <button
                          type="button"
                          onClick={handleCancel}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-500 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
            )}

            {/* Add Button */}
            {!showAddForm && (
                <button
                    onClick={() => setShowAddForm(true)}
                    className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition-colors mb-6"
                >
                  + Add Transaction
                </button>
            )}

            {/* Transactions List */}
            <div className="space-y-4">
              {loading && !transactions.length ? (
                  <div className="text-center py-8 text-yellow-300">
                    Loading transactions...
                  </div>
              ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-yellow-300">
                    No transactions found for this user.
                  </div>
              ) : (
                  transactions.map((transaction) => (
                      <div
                          key={transaction.id}
                          className="bg-gray-800 rounded-lg p-4 border border-gray-600 hover:border-yellow-400 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="mb-2">
                              <label className="text-yellow-300 text-sm font-medium">Transaction Hash:</label>
                              <a
                                href={`${transaction.transactionHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 font-mono text-sm break-all block"
                              >
                                {transaction.transactionHash}
                              </a>
                            </div>
                            <div className="mb-2">
                              <label className="text-yellow-300 text-sm font-medium">X Link:</label>
                              <a
                                  href={transaction.xlink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 text-sm break-all"
                              >
                                {transaction.xlink}
                              </a>
                            </div>
                            <div className="mb-2">
                              <label className="text-yellow-300 text-sm font-medium">Created At:</label>
                              <p className="text-yellow-200 text-sm">
                                {transaction.createdAt
                                  ? transaction.createdAt.seconds
                                    ? new Date(transaction.createdAt.seconds * 1000).toLocaleString()
                                    : new Date(transaction.createdAt).toLocaleString()
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-2">
                              <label className="text-yellow-300 text-sm font-medium">Updated At:</label>
                              <p className="text-yellow-200 text-sm">
                                {transaction.updatedAt
                                  ? transaction.updatedAt.seconds
                                    ? new Date(transaction.updatedAt.seconds * 1000).toLocaleString()
                                    : new Date(transaction.updatedAt).toLocaleString()
                                  : 'N/A'
                                }
                              </p>
                            </div>

                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                                onClick={() => handleEdit(transaction)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-500 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                                onClick={() => handleDelete(transaction.id)}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-500 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
  );
}


